import { GameComponent } from '@Core/GameComponent/GameComponent';
import { GameScene } from '@Core/GameScene/GameScene';
import { Game } from '@Game/Game';
import { EGameStoreKeys } from '@GameStore/GameStoreSchema';
import { ETurboModeLevels, ISpecialGames } from '@GFTypes/GameStoreTypes';
import { ESpecialGamesType } from '@GFTypes/SpecialGames';
import { takeAction } from '@Managers/ButtonManager/ButtonActions';
import EventsCenter from '@Managers/Events/EventsCenter';
import { EGameEvents } from '@Managers/Events/GameEvents';
import { Logger, LoggerLevel, LoggerSource } from '@Managers/Logger';
import { EGameStates } from '@Managers/StateManager/GameStates';
import { StateConditions } from '@Managers/StateManager/StateConditions';
import {
    SPECIAL_GAME_CONFIGS_GAMESTORE_KEYS,
    SPECIAL_GAME_GAMESTORE_DEFAULTS,
    SPECIAL_GAME_INTRO_FLAGS,
    SPECIAL_GAME_MODE_FLAGS
} from '../../Types/GameStoreTypes/SpecialGames';
import { getLastNotEndedSpecialGames, getSpecialGameDataByType, getSpecialGames } from './SpecialGamesHelper';
export class SpecialGames extends GameComponent {
    private readonly modeStack: ESpecialGamesType[] = [];
    constructor(public readonly scene: GameScene) {
        super(scene);
        Logger.log(LoggerLevel.Constructor, {
            class: SpecialGames.name,
            source: LoggerSource.Game,
            message: 'SpecialGames constructor'
        });

        EventsCenter.on(EGameStates.PRE_SPECIAL_GAMES_INTRO_STATE, this.onSpecialGamesIntroStart, this);
        EventsCenter.on(EGameStates.POST_SPECIAL_GAMES_INTRO_STATE, this.onSpecialGamesStart, this);
        EventsCenter.on(EGameEvents.CHANGE_REEL_COUNT, this.onSpecialGamesStart, this);
        EventsCenter.on(EGameStates.POST_SPECIAL_GAMES_OUTRO_STATE, this.onSpecialGamesOutroEnd, this);
        EventsCenter.on(EGameEvents.SPECIAL_GAMES_RESTORE, this.onSpecialGamesRestore, this);
    }

    private getSpecialGameData(specialGameType: ESpecialGamesType): ISpecialGames | undefined {
        const currentSpecialGames = getSpecialGames(this.gameStore);
        if (!currentSpecialGames) return;
        return getSpecialGameDataByType(currentSpecialGames, specialGameType);
    }

    private enterSpecialGameIntro(specialGameType: ESpecialGamesType) {
        const specialGameData = this.getSpecialGameData(specialGameType);
        if (!specialGameData) return;

        const specialGamesStoreKey = SPECIAL_GAME_INTRO_FLAGS.get(specialGameType)!;
        const isGameRestored = this.gameStore.get(EGameStoreKeys.IsGameRestored);
        const { gameTypeAfter } = isGameRestored ? this.gameStore.get(EGameStoreKeys.ResumedSpinOutcome)! : this.gameStore.get(EGameStoreKeys.SpinOutcome);
        this.gameStore.set(specialGamesStoreKey, true);
        this.gameStore.set(EGameStoreKeys.CurrentSpecialGamesType, specialGameType);
        this.gameStore.set(EGameStoreKeys.GameType, gameTypeAfter);
    }

    private exitSpecialGameIntro(specialGameType: ESpecialGamesType) {
        const specialGameData = this.getSpecialGameData(specialGameType);
        if (!specialGameData) return;

        const specialGamesStoreKey = SPECIAL_GAME_INTRO_FLAGS.get(specialGameType)!;
        this.gameStore.set(specialGamesStoreKey, false);
    }

    private enterSpecialGameMode(specialGameType: ESpecialGamesType, switchMode?: boolean): void {
        const specialGameData = this.getSpecialGameData(specialGameType);
        if (!specialGameData || this.modeStack.includes(specialGameType)) return;
        const isGameRestored = this.gameStore.get(EGameStoreKeys.IsGameRestored);
        const { gameTypeAfter } = isGameRestored ? this.gameStore.get(EGameStoreKeys.ResumedSpinOutcome)! : this.gameStore.get(EGameStoreKeys.SpinOutcome);
        const specialGamesStoreKey = SPECIAL_GAME_MODE_FLAGS.get(specialGameType)!;
        this.gameStore.set(specialGamesStoreKey, true);
        this.gameStore.set(EGameStoreKeys.CurrentSpecialGamesType, specialGameType);
        this.gameStore.set(EGameStoreKeys.GameType, gameTypeAfter);
        this.gameStore.set(EGameStoreKeys.TurboModeState, { specialGameLevel: ETurboModeLevels.NORMAL }, true);

        this.modeStack.push(specialGameType);
        takeAction(this.scene.game as Game, false);
        this.gameStore.set(EGameStoreKeys.GeneralAppState, {
            insufficientFunds: !this.gameStore.get(EGameStoreKeys.BetState).available && this.scene.game.betManager.getCurrentSpinBet() > 0
        });
        EventsCenter.emit(switchMode ? EGameEvents.SPECIAL_GAMES_MODE_SWITCH : EGameEvents.SPECIAL_GAMES_MODE_ENTRY);
    }

    private exitSpecialGameMode(): void {
        const currentSpecialGamesType = this.gameStore.get(EGameStoreKeys.CurrentSpecialGamesType);
        if (!currentSpecialGamesType) return;
        const { gameTypeAfter } = this.gameStore.get(EGameStoreKeys.SpinOutcome);
        const modeKey = SPECIAL_GAME_MODE_FLAGS.get(currentSpecialGamesType)!;
        const dataKey = SPECIAL_GAME_CONFIGS_GAMESTORE_KEYS.get(currentSpecialGamesType)!;
        this.gameStore.set(modeKey, false);
        this.gameStore.set(dataKey, SPECIAL_GAME_GAMESTORE_DEFAULTS.get(currentSpecialGamesType)!);
        this.gameStore.set(EGameStoreKeys.CurrentSpecialGamesType, undefined);
        this.gameStore.set(EGameStoreKeys.GameType, gameTypeAfter);
        this.gameStore.set(EGameStoreKeys.TurboModeState, { specialGameLevel: undefined }, true);
        this.modeStack.pop();
        this.gameStore.set(EGameStoreKeys.GeneralAppState, {
            insufficientFunds: !this.gameStore.get(EGameStoreKeys.BetState).available && this.scene.game.betManager.getCurrentSpinBet() > 0
        });
        if (this.modeStack.length > 1) this.enterSpecialGameMode(this.modeStack.at(-1)!, true);
        else EventsCenter.emit(EGameEvents.SPECIAL_GAMES_MODE_EXIT);
    }

    private onSpecialGamesIntroStart(): void {
        const currentSpecialGames = getLastNotEndedSpecialGames(this.gameStore);
        if (!currentSpecialGames) return;
        this.enterSpecialGameIntro(currentSpecialGames.type);
    }

    private onSpecialGamesStart(): void {
        const currentSpecialGames = getLastNotEndedSpecialGames(this.gameStore);
        if (!currentSpecialGames) return;
        this.exitSpecialGameIntro(currentSpecialGames.type);
        this.enterSpecialGameMode(currentSpecialGames.type);
    }

    private onSpecialGamesRestore(): void {
        const currentSpecialGames = getLastNotEndedSpecialGames(this.gameStore);
        if (!currentSpecialGames || currentSpecialGames.currentSpin === 0) return;
        this.enterSpecialGameMode(currentSpecialGames.type);
    }

    private onSpecialGamesOutroEnd(): void {
        this.exitSpecialGameMode();
        StateConditions.idleCondition.reset();
    }
}
